import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { DutiesMessages } from '../helpers/DutiesMessages';

import { PreQuoteDutiesDescription_viewer$key } from './__generated__/PreQuoteDutiesDescription_viewer.graphql';
import { PreQuoteDutiesDescription_shipmentQuote$key } from './__generated__/PreQuoteDutiesDescription_shipmentQuote.graphql';
import styles from './PreQuoteDutiesDescription.scss';

export type PreQuoteDutiesDescriptionProps = {
    viewer: PreQuoteDutiesDescription_viewer$key;
    shipmentQuote: PreQuoteDutiesDescription_shipmentQuote$key | null;
};

const PreQuoteDutiesDescription: FC<PreQuoteDutiesDescriptionProps> = ({
    viewer: viewerRef,
    shipmentQuote: shipmentQuoteRef,
}) => {
    //Surface arranging party on prequote description for better buyer experience
    const shipmentQuote = useFragment(
        graphql`
            fragment PreQuoteDutiesDescription_shipmentQuote on ShipmentQuote {
                isFromDealer
                isFromInternal
            }
        `,
        shipmentQuoteRef
    );

    const viewer = useFragment(
        graphql`
            fragment PreQuoteDutiesDescription_viewer on Viewer {
                customsDuties(
                    buyerId: $buyerId
                    itemId: $itemId
                    destinationCountry: $destinationCountry
                    shipmentQuoteId: $shipmentQuoteId
                ) {
                    crossCustomsBorder
                    incoterm
                }
            }
        `,
        viewerRef
    );

    //New customs description depends on whether transaction is cross customs border and incoterm
    const customsDutiesInfo = viewer?.customsDuties;
    const transactionIsDDP = customsDutiesInfo?.incoterm === 'DDP';
    const transactionIsCrossCustomsborder = customsDutiesInfo?.crossCustomsBorder;

    let currentDutiesMessage = null;
    if (transactionIsCrossCustomsborder) {
        currentDutiesMessage = transactionIsDDP ? DutiesMessages.DDP : DutiesMessages.DAP;
    }

    //Arranging party will always be 1stDibs or Seller if DAP
    const isFromDealer = shipmentQuote?.isFromDealer;
    const arrangingParty = isFromDealer ? (
        <FormattedMessage
            id="checkout.checkoutPrequoteDescription.arrangingPartySeller"
            defaultMessage="Seller"
        />
    ) : (
        '1stDibs'
    );

    return (
        <span className={styles.description}>
            <FormattedMessage
                id="checkout.PreQuoteDutiesDescription.deliveryMethodCopy"
                defaultMessage="Arranged by {arrangingParty}. {currentDutiesMessage}"
                values={{ arrangingParty, currentDutiesMessage }}
            />
        </span>
    );
};

export default PreQuoteDutiesDescription;
